/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'

import { cn } from '@/lib/utils'
import createGlobe, { COBEOptions } from 'cobe'
import { useTheme } from 'next-themes'
import { useCallback, useEffect, useRef, useState } from 'react'

const GLOBE_CONFIG_LIGHT: COBEOptions = {
  width: 800,
  height: 800,
  onRender: () => {},
  devicePixelRatio: 2,
  phi: 0,
  theta: 0.3,
  dark: 0,
  diffuse: 0.4,
  mapSamples: 16000,
  mapBrightness: 1.2,
  baseColor: [1, 1, 1], // Trắng
  markerColor: [251 / 255, 100 / 255, 21 / 255], // Cam #FB6415
  glowColor: [1, 1, 1], // Trắng
  markers: [
    { location: [14.5995, 120.9842], size: 0.03 },
    { location: [19.076, 72.8777], size: 0.1 },
    { location: [23.8103, 90.4125], size: 0.05 },
    { location: [30.0444, 31.2357], size: 0.07 },
    { location: [39.9042, 116.4074], size: 0.08 },
    { location: [-23.5505, -46.6333], size: 0.1 },
    { location: [19.4326, -99.1332], size: 0.1 },
    { location: [40.7128, -74.006], size: 0.1 },
    { location: [34.6937, 135.5022], size: 0.05 },
    { location: [41.0082, 28.9784], size: 0.06 }
  ]
}

const GLOBE_CONFIG_DARK: COBEOptions = {
  ...GLOBE_CONFIG_LIGHT,
  dark: 1.5, // Tăng độ tối
  baseColor: [0.2, 0.2, 0.2], // Xám đậm #333333
  markerColor: [1, 0.6, 0.2], // Cam nhạt #FF9933
  glowColor: [0.15, 0.15, 0.15] // Xám sáng #B3B3B3
}

export default function Globe({
  className,
  config = GLOBE_CONFIG_LIGHT
}: {
  className?: string
  config?: COBEOptions
}) {
  let phi = 0
  let width = 0
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const pointerInteracting = useRef(null)
  const pointerInteractionMovement = useRef(0)
  const [r, setR] = useState(0)
  const { resolvedTheme } = useTheme()

  const updatePointerInteraction = (value: any) => {
    pointerInteracting.current = value
    if (canvasRef.current) {
      canvasRef.current.style.cursor = value ? 'grabbing' : 'grab'
    }
  }

  const updateMovement = (clientX: any) => {
    if (pointerInteracting.current !== null) {
      const delta = clientX - pointerInteracting.current
      pointerInteractionMovement.current = delta
      setR(delta / 200)
    }
  }

  const onRender = useCallback(
    (state: Record<string, any>) => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (!pointerInteracting.current) phi += 0.005
      state.phi = phi + r
      state.width = width * 2
      state.height = width * 2
    },
    [r]
  )

  const onResize = () => {
    if (canvasRef.current) {
      width = canvasRef.current.offsetWidth
    }
  }

  useEffect(() => {
    window.addEventListener('resize', onResize)
    onResize()

    const currentConfig = resolvedTheme === 'dark' ? GLOBE_CONFIG_DARK : GLOBE_CONFIG_LIGHT
    const globe = createGlobe(canvasRef.current!, {
      ...config,
      ...currentConfig,
      width: width * 2,
      height: width * 2,
      onRender
    })

    setTimeout(() => (canvasRef.current!.style.opacity = '1'))
    return () => globe.destroy()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedTheme])

  return (
    <div
      className={cn(
        'absolute mx-auto aspect-[1/1] w-full max-sm:mt-7 mt-10 max-sm:max-w-[200px] max-w-[500px]',
        className
      )}
    >
      <canvas
        className={cn(
          'size-full opacity-0 transition-opacity duration-500 [contain:layout_paint_size] dark:opacity-90'
        )}
        ref={canvasRef}
        onPointerDown={(e) =>
          updatePointerInteraction(e.clientX - pointerInteractionMovement.current)
        }
        onPointerUp={() => updatePointerInteraction(null)}
        onPointerOut={() => updatePointerInteraction(null)}
        onMouseMove={(e) => updateMovement(e.clientX)}
        onTouchMove={(e) => e.touches[0] && updateMovement(e.touches[0].clientX)}
      />
    </div>
  )
}
